import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { StoresContext } from '../../provider/StoreProvider'

import { useNavigate } from '../../hooks/useNavigate'
import useParamId from '../../hooks/useParamId'
import { useUserInfo } from '../../hooks/useUserInfo'

import { getUserProductDetail, getUserProductPurchases } from '../../api/product'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, CenterWrap, ColumnItem, RowBetween } from '../../components/common'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { ProductInfo } from './components/ProductInfo'

import { Color } from '../../theme/color'

import { ReactComponent as ArrowRightGrayIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray_18.svg'

export function ProductDetail() {
  const {
    commonStore: { setInvalidAccess },
  } = useContext(StoresContext)

  const navigate = useNavigate()
  const userProductId = useParamId()
  const { isLogin, isCheckingLogin } = useUserInfo()

  const userProductDetail = useQuery({
    queryKey: [QUERY_KEY.userProduct, { userProductId }],
    queryFn: async () => await getUserProductDetail(userProductId as number),
    enabled: typeof userProductId === 'number',
  })
  const userProductPurchases = useQuery({
    queryKey: [QUERY_KEY.userProductPurchases, { userProductId }],
    queryFn: async () => await getUserProductPurchases(userProductId as number),
    enabled: typeof userProductId === 'number',
  })

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (userProductDetail.isError || userProductPurchases.isError) {
      setInvalidAccess(true)
    }
  }, [userProductDetail.isError, userProductPurchases.isError])

  if (
    typeof userProductId !== 'number' ||
    userProductDetail.isPending ||
    userProductDetail.isError ||
    userProductPurchases.isPending ||
    userProductPurchases.isError
  ) {
    return (
      <Wrap>
        <Header type="SUB" title="자산 상세" />
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </Wrap>
    )
  }

  const {
    status,
    product: { id: productId, imageUri, artist, name },
  } = userProductDetail.data

  return (
    <Wrap>
      <Header type="SUB" title="자산 상세" />
      <Content>
        <ProductInfoWrap>
          <ProductInfoCard>
            <ProductInfoContent $gap={16}>
              <ProductImage>
                <img src={imageUri} alt={name} />
              </ProductImage>
              <ColumnItem $gap={4}>
                <Text size={18} weight={600}>
                  {artist}
                </Text>
                <Text size={14} color="text_secondary">
                  {name}
                </Text>
              </ColumnItem>
              <ProductDetailLink>
                <Text
                  size={14}
                  weight={500}
                  color="text_secondary"
                  onClick={() => navigate(`/market/product/${productId}`)}
                >
                  작품 상세 보기
                </Text>
                <ArrowRightGrayIcon />
              </ProductDetailLink>
            </ProductInfoContent>
            <DivideLine />
            <ProductInfo userProductId={userProductId} type={status} />
          </ProductInfoCard>
        </ProductInfoWrap>
        <HistoryContainer>
          <Text size={18} weight={600} block={true}>
            투자 내역
          </Text>
          <ReverseContainer>
            {userProductPurchases.data.map(({ id, createdDate, count, purchaseTypeLabel, amount }) => {
              const YMD = dayjs(createdDate).format('YY.MM.DD')
              const HM = dayjs(createdDate).format('HH:mm')

              return (
                <BreakdownContainer key={id}>
                  <TransactionContainer>
                    <ColumnItem $gap={6}>
                      <Text size={10} color="text_secondary">
                        {YMD}
                      </Text>
                      <Text size={10} color="text_secondary">
                        {HM}
                      </Text>
                    </ColumnItem>
                    <ColumnItem $gap={4}>
                      <RowBetween>
                        <Text size={16} weight={500}>
                          {purchaseTypeLabel}
                        </Text>
                        <Text size={18} weight={600}>
                          {Math.abs(count).toLocaleString() ?? 0}주
                        </Text>
                      </RowBetween>
                      <Text size={10} color="text_secondary" $textAlign="right">
                        {Math.abs(amount ?? 0).toLocaleString() ?? 0} 원
                      </Text>
                    </ColumnItem>
                  </TransactionContainer>
                </BreakdownContainer>
              )
            })}
          </ReverseContainer>
        </HistoryContainer>
      </Content>
    </Wrap>
  )
}

const ProductInfoWrap = styled.div`
  padding: 16px;
  background-color: ${Color.gray_1};
`

const ProductInfoContent = styled(AlignCenterItem)`
  position: relative;
`

const ProductImage = styled.div`
  overflow: hidden;
  width: 100px;
  aspect-ratio: 1/1.3;
  border-radius: 8px;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProductDetailLink = styled(AlignCenterItem)`
  position: absolute;
  right: 0;
  bottom: 0;
  border: none;
  outline: none;
  cursor: pointer;
`

const HistoryContainer = styled.div`
  padding: 24px;
`

const ReverseContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
`

const BreakdownContainer = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid ${Color.line};
`

const TransactionContainer = styled(RowBetween)`
  gap: 32px;
  > * {
    &:first-child {
      flex-shrink: 0;
    }
    &:last-child {
      width: 100%;
    }
  }
`
